import type { CompletionStateDto, DataCleanupStateDto, DataEntityDto, DataModelState, DataTypeDto, DocumentLinksStateDto, RequiredDocumentsState, Site, TypeRereadStateDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, ErrorText, PageTitle, SectionLoader, Select } from '../../ui';

/** Elementi per pagina in ogni tipo. */
const PAGE = 25;

/** Testo confrontabile: minuscolo e senza accenti. */
const fold = (v: unknown) =>
  String(v ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const entityName = (e: DataEntityDto) => e.nome || String(e.attributi.nome ?? e.rif);
const byName = (a: DataEntityDto, b: DataEntityDto) => entityName(a).localeCompare(entityName(b), 'it', { sensitivity: 'base', numeric: true });

/** Un'entità corrisponde alla ricerca se la contiene un suo valore, una sede o un'attività a cui è legata. */
const entityMatches = (e: DataEntityDto, q: string) =>
  fold(e.nome).includes(q) ||
  Object.values(e.attributi).some((v) => fold(v).includes(q)) ||
  e.legami.some((l) => l.stato === 'attivo' && (fold(l.sede).includes(q) || fold(l.attivita).includes(q)));

/** Documenti mancanti per entità, dalle voci dei documenti obbligatori che valgono per ogni entità di un tipo. */
export function missingDocs(required: RequiredDocumentsState | undefined, rif: string): string[] {
  return (required?.data ?? []).filter((d) => d.stato !== 'non_riguarda' && d.elementi?.some((e) => e.chiave === rif && e.stato === 'mancante')).map((d) => d.titolo);
}

/** Legami detti dalla persona: per ogni sede scelta le attività (vuoto = da attribuire); `null` = tutta l'impresa. */
type LinkChoice = Map<string | null, Set<string>>;

const choiceOf = (e: DataEntityDto | null): LinkChoice => {
  const m: LinkChoice = new Map();
  for (const l of e?.legami ?? []) {
    if (l.stato !== 'attivo') continue;
    const acts = m.get(l.sede_id) ?? new Set<string>();
    if (l.attivita_id) acts.add(l.attivita_id);
    m.set(l.sede_id, acts);
  }
  return m;
};

const choiceKey = (c: LinkChoice) =>
  [...c]
    .map(([s, a]) => `${s ?? 'impresa'}:${[...a].sort().join(',')}`)
    .sort()
    .join('|');

/** Sedi e attività di un'entità in una cella (ADR 0057): «da attribuire» quando non si sa. */
export function EntityLinks({ e }: { e: DataEntityDto }) {
  const active = e.legami.filter((l) => l.stato === 'attivo');
  if (!active.length) return <span className="text-amber-700">sede da attribuire</span>;
  const bySite = new Map<string, typeof active>();
  for (const l of active) bySite.set(l.sede_id ?? '', [...(bySite.get(l.sede_id ?? '') ?? []), l]);
  return (
    <span className="flex flex-col gap-0.5">
      {[...bySite.values()].map((ls) => (
        <span key={ls[0]!.sede_id ?? 'impresa'} title={ls.map((l) => `${l.fonte}${l.motivo ? ` — ${l.motivo}` : ''}`).join('\n')}>
          <span className="font-medium">{ls[0]!.sede ?? 'Tutta l’impresa'}</span>
          {ls[0]!.sede_id &&
            (ls.some((l) => !l.attivita_id) ? (
              <span className="text-amber-700"> · attività da attribuire</span>
            ) : (
              <span className="text-ink/60"> · {ls.map((l) => l.attivita).join(', ')}</span>
            ))}
        </span>
      ))}
    </span>
  );
}

/** Scelta di sedi e attività: le attività offerte sono quelle della sede. */
function LinksEditor({ sites, value, onChange }: { sites: Site[]; value: LinkChoice; onChange: (v: LinkChoice) => void }) {
  const toggleSite = (id: string | null) => {
    const next = new Map(value);
    if (next.has(id)) next.delete(id);
    else next.set(id, new Set());
    onChange(next);
  };
  const toggleActivity = (site: string, act: string) => {
    const next = new Map(value);
    const acts = new Set(next.get(site) ?? []);
    if (acts.has(act)) acts.delete(act);
    else acts.add(act);
    next.set(site, acts);
    onChange(next);
  };
  return (
    <div className="mt-4 border-t border-black/[0.06] pt-3 text-xs">
      <span className="mb-1.5 block font-medium text-ink/80">Sedi e attività con cui ha a che fare</span>
      <div className="space-y-1">
        {sites.map((site) => (
          <div key={site.id}>
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={value.has(site.id)} onChange={() => toggleSite(site.id)} />
              {site.name}
            </label>
            {value.has(site.id) && (
              <div className="ml-5 flex flex-wrap gap-x-3 gap-y-0.5">
                {site.activities.length ? (
                  site.activities.map((a) => (
                    <label key={a.id} className="flex items-center gap-1 text-ink/70">
                      <input type="checkbox" checked={value.get(site.id)!.has(a.id)} onChange={() => toggleActivity(site.id, a.id)} />
                      {a.name}
                    </label>
                  ))
                ) : (
                  <span className="text-ink/45">nessuna attività nota in questa sede</span>
                )}
                {!!site.activities.length && !value.get(site.id)!.size && <span className="text-amber-700">attività da attribuire</span>}
              </div>
            )}
          </div>
        ))}
        <label className="flex items-center gap-1.5" title="Solo se il rapporto è con l’impresa come soggetto, non con ciò che si fa in una sede">
          <input type="checkbox" checked={value.has(null)} onChange={() => toggleSite(null)} />
          Tutta l’impresa (rapporto con l’impresa come soggetto)
        </label>
      </div>
    </div>
  );
}

export function EntityForm({
  type,
  entity,
  sites,
  onSaved,
  onCancel,
}: {
  type: DataTypeDto;
  entity: DataEntityDto | null;
  sites: Site[];
  onSaved: (s: DataModelState) => void;
  onCancel: () => void;
}) {
  const { t } = useTenant();
  const [links, setLinks] = useState<LinkChoice>(() => choiceOf(entity));
  const linksChanged = choiceKey(links) !== choiceKey(choiceOf(entity));
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(type.attributi.map((a) => [a.nome, entity?.attributi[a.nome] != null ? String(entity.attributi[a.nome]) : ''])));
  const save = useMutation({
    mutationFn: () =>
      api<DataModelState>(t(`/data-model/types/${type.chiave}/entities`), {
        // Solo i valori cambiati: quelli del motore che la persona non tocca restano con la loro fonte.
        body: {
          rif: entity?.rif ?? null,
          attributi: Object.fromEntries(Object.entries(values).filter(([k, v]) => v.trim() && v !== String(entity?.attributi[k] ?? ''))),
          // Sedi e attività solo se la persona le ha cambiate: diventano sue (ADR 0057).
          legami: linksChanged ? [...links].filter(([sede]) => sede === null || sites.some((x) => x.id === sede)).map(([sede, acts]) => ({ sede, attivita: [...acts] })) : null,
        },
      }),
    onSuccess: onSaved,
  });
  const remove = useMutation({
    mutationFn: () => api<DataModelState>(t(`/data-model/types/${type.chiave}/entities/${encodeURIComponent(entity!.rif)}/delete`), { body: {} }),
    onSuccess: onSaved,
  });
  return (
    <div className="rounded-xl border border-accent/20 bg-accent/[0.03] p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        {type.attributi.map((a) => (
          <label key={a.nome} className="block text-xs">
            <span className="mb-1 block font-medium text-ink/80" title={a.perche ?? undefined}>
              {a.etichetta}
            </span>
            <input
              className="w-full rounded-[10px] border border-black/[0.08] bg-white px-3 py-2 text-sm outline-none transition-colors placeholder:text-ink/35 focus:border-accent/50 focus:ring-2 focus:ring-accent/15 disabled:bg-black/[0.03] disabled:text-ink/60"
              type={a.tipo === 'data' ? 'date' : a.tipo === 'numero' ? 'number' : 'text'}
              value={values[a.nome] ?? ''}
              disabled={!!entity && a.nome === 'nome'}
              placeholder={a.descrizione ?? ''}
              onChange={(e) => setValues({ ...values, [a.nome]: e.target.value })}
            />
            {entity?.fonti[a.nome] && <span className="text-[11px] text-ink/45">da: {entity.fonti[a.nome]}</span>}
          </label>
        ))}
      </div>
      {!!sites.length && <LinksEditor sites={sites} value={links} onChange={setLinks} />}
      <ErrorText>{save.error ? errorMessage(save.error) : remove.error ? errorMessage(remove.error) : ''}</ErrorText>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Salvo…' : 'Salva'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Annulla
        </Button>
        {entity && (
          <Button variant="ghost" className="ml-auto text-red-700" disabled={remove.isPending} onClick={() => remove.mutate()}>
            Togli
          </Button>
        )}
      </div>
    </div>
  );
}

function TypeCard({
  type,
  required,
  sites,
  filter,
  query,
  onState,
  alwaysOpen = false,
  allTypes = [],
}: {
  type: DataTypeDto;
  required: RequiredDocumentsState | undefined;
  sites: Site[];
  filter: { site: string; activity: string };
  /** Testo cercato, già normalizzato con `fold`. */
  query: string;
  onState: (s: DataModelState) => void;
  /** Pagina del solo tipo (ADR 0112): sempre aperto, anche se la ricerca non trova nulla. */
  alwaysOpen?: boolean;
  /** Tutti i tipi del modello: i valori che nominano un'altra cosa diventano collegamenti alla sua scheda. */
  allTypes?: DataTypeDto[];
}) {
  const { tenant, t, isManager } = useTenant();
  // Chiuso all'apertura della pagina; durante una ricerca si apre da solo, finché la persona non lo richiude.
  const [toggle, setToggle] = useState({ query: '', open: false });
  // Ricerca per colonna, nell'intestazione dell'elenco: chiave dell'attributo (o «legami») → testo cercato.
  const [cols, setCols] = useState<Record<string, string>>({});
  const colQuery = Object.entries(cols)
    .map(([k, v]) => [k, fold(v.trim())] as const)
    .filter(([, v]) => v);
  const view = `${query}|${filter.site}|${filter.activity}|${JSON.stringify(colQuery)}`;
  const [page, setPage] = useState({ view, n: 0 });
  const status = useMutation({
    mutationFn: (stato: 'attivo' | 'non_serve') => api<DataModelState>(t(`/data-model/types/${type.chiave}/status`), { body: { stato } }),
    onSuccess: onState,
  });
  const complete = useMutation({
    mutationFn: (completo: boolean) => api<DataModelState>(t(`/data-model/types/${type.chiave}/complete`), { body: { completo } }),
    onSuccess: onState,
  });
  // Unione dei doppioni (ADR 0168): conferma o rifiuto della proposta, annullamento di un'unione.
  const merge = useMutation({
    mutationFn: (into: string | null) => api<DataModelState>(t(`/data-model/types/${type.chiave}/merge-type`), { body: { in: into } }),
    onSuccess: onState,
  });
  const unmerge = useMutation({ mutationFn: () => api<DataModelState>(t(`/data-model/types/${type.chiave}/unmerge-type`), { body: {} }), onSuccess: onState });
  // Più voci insieme in un altro tipo (ADR 0168): le caselle dell'elenco e la barra sopra.
  const [picked, setPicked] = useState<string[]>([]);
  const moveMany = useMutation({
    mutationFn: (to: string) => api<DataModelState & { spostate: number }>(t(`/data-model/types/${type.chiave}/move-entities`), { body: { rif: picked, in: to } }),
    onSuccess: (s) => {
      setPicked([]);
      onState(s);
    },
  });
  const togglePick = (rif: string) => setPicked((p) => (p.includes(rif) ? p.filter((x) => x !== rif) : [...p, rif]));
  const merged = type.stato === 'unito';
  const off = type.stato === 'non_serve' || merged;
  const labelOf = (key: string | null) => allTypes.find((x) => x.chiave === key)?.etichetta ?? key ?? '';
  const incomplete = type.entita.filter((e) => e.mancanti.length).length;
  const unassigned = type.entita.filter((e) => e.da_attribuire).length;
  // Filtro per sede e attività (ADR 0057): «tutta l'impresa» vale per ogni sede.
  const inFilter = type.entita.filter((e) => {
    if (!filter.site) return true;
    const active = e.legami.filter((l) => l.stato === 'attivo');
    if (filter.site === 'da_attribuire') return e.da_attribuire;
    if (filter.activity) return active.some((l) => l.sede_id === filter.site && l.attivita_id === filter.activity);
    return active.some((l) => l.sede_id === filter.site || l.sede_id === null);
  });
  // Se la ricerca trova il tipo stesso si vedono tutte le sue entità, altrimenti solo quelle che la contengono.
  const typeMatches = !query || fold(type.etichetta).includes(query) || fold(type.descrizione).includes(query);
  const shown = (typeMatches ? inFilter : inFilter.filter((e) => entityMatches(e, query)))
    .filter((e) =>
      colQuery.every(([k, v]) =>
        k === 'legami'
          ? e.legami.some((l) => l.stato === 'attivo' && (fold(l.sede).includes(v) || fold(l.attivita).includes(v)))
          : fold(k === 'nome' ? entityName(e) : e.attributi[k]).includes(v),
      ),
    )
    .sort(byName);
  // Il valore che nomina un'altra cosa del modello porta alla sua scheda (uno per nome, più nomi separati da «; »).
  const linked = (key: string, value: unknown) => {
    const other = allTypes.find((x) => x.chiave === key);
    if (!other || value === null || value === undefined || value === '') return null;
    const names = String(value).split(/;\s*/);
    return names.map((name, i) => {
      const hit = other.entita.find((x) => fold(entityName(x)) === fold(name) || x.altri_nomi.some((o) => fold(o) === fold(name)));
      return (
        <span key={`${name}/${i}`}>
          {i > 0 && '; '}
          {hit ? (
            <a className="text-accent hover:underline" href={`#/t/${tenant.id}/e/${key}/${encodeURIComponent(hit.rif)}`} onClick={(ev) => ev.stopPropagation()}>
              {name}
            </a>
          ) : (
            name
          )}
        </span>
      );
    });
  };
  const expanded = alwaysOpen || (toggle.query === query ? toggle.open : !!query);
  const pages = Math.max(1, Math.ceil(shown.length / PAGE));
  const n = page.view === view ? Math.min(page.n, pages - 1) : 0;
  const pageRows = shown.slice(n * PAGE, (n + 1) * PAGE);
  if (!alwaysOpen && query && !typeMatches && !shown.length) return null;
  return (
    <Card className={off ? 'opacity-60' : ''}>
      <div className="flex flex-wrap items-start gap-2">
        <button className="min-w-0 flex-1 text-left" aria-expanded={expanded} disabled={alwaysOpen} onClick={() => setToggle({ query, open: !expanded })}>
          <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            {/* Nella pagina del tipo nome e descrizione sono già nel titolo. */}
            {!alwaysOpen && <span className={`inline-block w-3 text-ink/45 transition-transform ${expanded ? 'rotate-90' : ''}`}>›</span>}
            {!alwaysOpen && type.etichetta}
            {!off && <Badge tone="blue">{type.entita.length}</Badge>}
            {!off && query && !typeMatches && <Badge tone="green">{shown.length} trovati</Badge>}
            {!off && incomplete > 0 && <Badge tone="amber">{incomplete} da completare</Badge>}
            {!off && unassigned > 0 && <Badge tone="amber">{unassigned} senza sede o attività</Badge>}
            {/* Nella pagina del tipo (ADR 0112) non si confermano l'elenco né il tipo: lì si aggiunge e basta. */}
            {!off && !alwaysOpen && (type.completo_il ? <Badge tone="green">elenco completo</Badge> : <Badge>elenco da confermare</Badge>)}
            {type.stato === 'non_serve' && <Badge>Non serve</Badge>}
            {merged && <Badge>Unito in «{labelOf(type.unito_in)}»</Badge>}
          </h2>
          {expanded && !alwaysOpen && type.descrizione && <p className={`mt-0.5 ${alwaysOpen ? '' : 'pl-5'} text-xs text-ink/65`}>{type.descrizione}</p>}
          {expanded && (
            <p className={`mt-0.5 ${alwaysOpen ? '' : 'pl-5'} text-xs text-ink/50`}>
              <span className="font-medium">Perché:</span> {type.perche}
            </p>
          )}
        </button>
        {isManager && (
          <div className="flex shrink-0 gap-1">
            {!off && (
              // La nuova voce si compila nella sua pagina, come la scheda di una voce esistente.
              <Button className="inline-flex items-center gap-1.5 !py-1.5" onClick={() => navigate(`/t/${tenant.id}/nuova/${type.chiave}`)}>
                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M8 3v10M3 8h10" />
                </svg>
                Aggiungi
              </Button>
            )}
            {!off && !alwaysOpen && (
              <Button
                variant="ghost"
                disabled={complete.isPending}
                title={type.completo_il ? 'Riapri l’elenco: mancano delle voci' : 'Conferma che non ne mancano'}
                onClick={() => complete.mutate(!type.completo_il)}
              >
                {type.completo_il ? 'Ne mancano' : 'È completo'}
              </Button>
            )}
            {!alwaysOpen && merged && (
              <Button variant="ghost" disabled={unmerge.isPending} title="Il tipo torna attivo e le sue voci tornano con lui, con valori, documenti e legami" onClick={() => unmerge.mutate()}>
                Annulla l’unione
              </Button>
            )}
            {!alwaysOpen && !merged && (
              <Button variant="ghost" disabled={status.isPending} onClick={() => status.mutate(off ? 'attivo' : 'non_serve')}>
                {off ? 'Serve' : 'Non serve'}
              </Button>
            )}
          </div>
        )}
      </div>
      {isManager && !off && type.unione_proposta && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded border border-amber-300/60 bg-amber-50/60 px-3 py-2 text-xs dark:bg-amber-900/20">
          <span className="min-w-0 flex-1">
            Sembra lo stesso tipo di «{labelOf(type.unione_proposta.in)}»{type.unione_proposta.perche ? `: ${type.unione_proposta.perche}` : ''}. Decidi tu: tra soggetti il sistema non unisce da solo.
          </span>
          <Button disabled={merge.isPending} onClick={() => merge.mutate(type.unione_proposta!.in)}>
            Unisci in «{labelOf(type.unione_proposta.in)}»
          </Button>
          <Button variant="ghost" disabled={merge.isPending} onClick={() => merge.mutate(null)}>
            Sono diversi
          </Button>
        </div>
      )}
      <ErrorText>{status.error ? errorMessage(status.error) : complete.error ? errorMessage(complete.error) : merge.error ? errorMessage(merge.error) : unmerge.error ? errorMessage(unmerge.error) : ''}</ErrorText>
      {!off && expanded && (
        <>
          {isManager && picked.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded border border-accent/30 bg-accent/5 px-3 py-2 text-xs">
              <span className="font-medium">{picked.length} selezionate</span>
              <Select
                value=""
                disabled={moveMany.isPending}
                aria-label="Sposta le selezionate in un altro tipo"
                onChange={(ev) => {
                  const to = ev.target.value;
                  const label = allTypes.find((x) => x.chiave === to)?.etichetta;
                  if (to && window.confirm(`Spostare ${picked.length} voci in «${label}»? Valori, documenti e legami vanno con loro.`)) moveMany.mutate(to);
                }}
              >
                <option value="">{moveMany.isPending ? 'Sposto…' : 'Sposta le selezionate in…'}</option>
                {allTypes
                  .filter((x) => x.stato === 'attivo' && x.chiave !== type.chiave)
                  .sort((a, b) => Number(b.soggetto_esterno === type.soggetto_esterno) - Number(a.soggetto_esterno === type.soggetto_esterno) || a.etichetta.localeCompare(b.etichetta, 'it'))
                  .map((x) => (
                    <option key={x.chiave} value={x.chiave}>
                      {x.etichetta}
                    </option>
                  ))}
              </Select>
              <button className="text-accent hover:underline" onClick={() => setPicked([])}>
                Deseleziona
              </button>
              {moveMany.error && <ErrorText>{errorMessage(moveMany.error)}</ErrorText>}
            </div>
          )}
          {type.entita.length ? (
            <div className="mt-3 overflow-x-auto">
              <table className="bk-list min-w-max">
                <thead>
                  <tr>
                    {isManager && (
                      <th className="w-6">
                        <input
                          type="checkbox"
                          aria-label="Seleziona le voci di questa pagina"
                          checked={pageRows.length > 0 && pageRows.every((e) => picked.includes(e.rif))}
                          onChange={(ev) => setPicked((p) => (ev.target.checked ? [...new Set([...p, ...pageRows.map((e) => e.rif)])] : p.filter((x) => !pageRows.some((e) => e.rif === x))))}
                        />
                      </th>
                    )}
                    {type.attributi.map((a) => (
                      <th key={a.nome} title={[a.descrizione, a.perche ? `Serve per: ${a.perche}` : null].filter(Boolean).join(' · ')}>
                        {a.etichetta}
                        {a.riferisce && <span className="ml-1 font-normal opacity-60">→ {a.riferisce === 'persona' ? 'persone' : (allTypes.find((x) => x.chiave === a.riferisce)?.etichetta ?? a.riferisce).toLowerCase()}</span>}
                      </th>
                    ))}
                    <th>Sedi e attività</th>
                    <th>Documenti</th>
                  </tr>
                  <tr>
                    {[...type.attributi.map((a) => a.nome), 'legami'].map((k) => (
                      <th key={k}>
                        <input placeholder="Cerca…" value={cols[k] ?? ''} onChange={(ev) => setCols({ ...cols, [k]: ev.target.value })} />
                      </th>
                    ))}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((e) => {
                    const missing = missingDocs(required, e.rif);
                    return (
                      // La riga apre la scheda (ADR 0059), dove si modifica.
                      <tr key={e.rif} className="cursor-pointer" onClick={() => navigate(`/t/${tenant.id}/e/${type.chiave}/${encodeURIComponent(e.rif)}`)}>
                        {isManager && (
                          <td onClick={(ev) => ev.stopPropagation()}>
                            <input type="checkbox" aria-label={`Seleziona ${entityName(e)}`} checked={picked.includes(e.rif)} onChange={() => togglePick(e.rif)} />
                          </td>
                        )}
                        {type.attributi.map((a) => (
                          <td key={a.nome} className={a.nome === 'nome' ? 'font-medium' : ''} title={e.fonti[a.nome] ? `da: ${e.fonti[a.nome]}` : undefined}>
                            {a.nome === 'nome' ? (
                              // Il nome apre la scheda con tutti i collegamenti (ADR 0059).
                              <a
                                className="text-accent hover:underline"
                                href={`#/t/${tenant.id}/e/${type.chiave}/${encodeURIComponent(e.rif)}`}
                                onClick={(ev) => ev.stopPropagation()}
                              >
                                {entityName(e)}
                              </a>
                            ) : a.riferisce && a.riferisce !== 'persona' && allTypes.some((x) => x.chiave === a.riferisce) && e.attributi[a.nome] ? (
                              linked(a.riferisce, e.attributi[a.nome])
                            ) : typeof e.attributi[a.nome] === 'string' && /^https?:\/\//.test(String(e.attributi[a.nome])) ? (
                              <a className="text-accent underline" href={String(e.attributi[a.nome])} target="_blank" rel="noreferrer" onClick={(ev) => ev.stopPropagation()}>
                                apri
                              </a>
                            ) : (
                              (e.attributi[a.nome] ?? <span className="text-amber-700">—</span>)
                            )}
                          </td>
                        ))}
                        <td>
                          <EntityLinks e={e} />
                        </td>
                        <td>
                          {missing.length ? (
                            <button
                              className="text-amber-700 underline"
                              title={missing.join(', ')}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                navigate(`/t/${tenant.id}/archivio`);
                              }}
                            >
                              {missing.length === 1 ? `manca: ${missing[0]}` : `mancano ${missing.length}`}
                            </button>
                          ) : (
                            <span className="text-ink/40">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!shown.length && <p className="py-3 text-center text-xs text-ink/50">Nessuno{query ? ' per questa ricerca' : ' con questo filtro'}.</p>}
              {shown.length > PAGE && (
                <div className="mt-2 flex items-center gap-2 text-xs text-ink/55">
                  <span className="flex-1">
                    {n * PAGE + 1}–{n * PAGE + pageRows.length} di {shown.length}
                  </span>
                  <Button variant="ghost" disabled={n === 0} onClick={() => setPage({ view, n: n - 1 })}>
                    Precedenti
                  </Button>
                  <Button variant="ghost" disabled={n + 1 >= pages} onClick={() => setPage({ view, n: n + 1 })}>
                    Successivi
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <p className="mt-2 text-xs text-ink/55">Nessuno ancora: li trova la lettura di documenti, posta e dati importati, oppure aggiungili tu.</p>
          )}
        </>
      )}
    </Card>
  );
}

/** Un lavoro in background con stima prima e avanzamento (ADR 0060, 0073): stima senza motore, poi avvio su conferma. */
function JobPanel<S extends { fase: string; stima: { costo_usd: number } | null; totale: number; errore: string | null }>({
  path,
  title,
  intro,
  running,
  estimating,
  stopped,
  hidden,
  summary,
}: {
  path: string;
  title: string;
  intro: string;
  /** Fase del lavoro in corso. */
  running: string;
  /** Fase della stima in corso, se la stima è in background. */
  estimating?: string;
  /** Fase del lavoro fermato a metà (es. tetto di spesa): si riprende da dove era. */
  stopped?: string;
  /** Quando il pannello non serve. */
  hidden?: (s: S) => boolean;
  summary: (s: S) => ReactNode;
}) {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const key = ['tenant', tenant.id, 'data-model', path];
  const q = useTenantQuery<S>(['data-model', path], `/data-model/${path}`, { refetchInterval: (s) => (s?.fase === running || s?.fase === estimating ? 3000 : false) });
  const update = (s: S) => queryClient.setQueryData(key, s);
  const estimate = useMutation({ mutationFn: () => api<S>(t(`/data-model/${path}/estimate`), { body: {} }), onSuccess: update });
  const start = useMutation({ mutationFn: () => api<S>(t(`/data-model/${path}/start`), { body: {} }), onSuccess: update });
  const s = q.data;
  if (!s || hidden?.(s)) return null;
  const busy = s.fase === running || s.fase === estimating;
  return (
    <Card>
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold">{title}</h2>
          <p className="mt-0.5 text-xs text-ink/60">{intro}</p>
          <div className="mt-2 text-xs">{summary(s)}</div>
          <ErrorText>{s.errore ?? (estimate.error ? errorMessage(estimate.error) : start.error ? errorMessage(start.error) : '')}</ErrorText>
        </div>
        <div className="flex shrink-0 gap-1">
          {!busy && (
            <Button variant="ghost" disabled={estimate.isPending} onClick={() => estimate.mutate()}>
              {s.stima ? 'Rifai la stima' : 'Stima'}
            </Button>
          )}
          {s.fase === 'pronto' && !!s.totale && (
            <Button disabled={start.isPending} onClick={() => start.mutate()}>
              Avvia (≈ ${s.stima?.costo_usd.toFixed(2)})
            </Button>
          )}
          {!!stopped && s.fase === stopped && (
            <Button disabled={start.isPending} onClick={() => start.mutate()}>
              Riprendi
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

const progress = (fatti: number, totale: number) => `${fatti} di ${totale} (${totale ? Math.round((fatti / totale) * 100) : 0}%)`;

/** Collegamento dei documenti già letti ai rapporti con i soggetti (ADR 0060). */
function DocumentLinksPanel() {
  return (
    <JobPanel<DocumentLinksStateDto>
      path="document-links"
      title="Documenti e posta legati ai rapporti"
      intro="Le letture nuove legano ogni documento e messaggio al cliente, al fornitore o all’altro soggetto che riguarda. Per quelli già letti il sistema trova chi nominano e, se lo confermi, il modello economico dice a quale rapporto appartengono."
      running="collego"
      estimating="stima"
      summary={(s) =>
        s.fase === 'stima' ? (
          'Conto i documenti e i messaggi da collegare…'
        ) : s.fase === 'pronto' && s.stima ? (
          <>
            Da collegare: <b>{s.stima.documenti}</b> documenti e <b>{s.stima.messaggi}</b> messaggi (circa {Math.round(s.stima.token / 1000)} mila token,{' '}
            <b>circa ${s.stima.costo_usd.toFixed(2)}</b>). Esclusi {s.stima.esclusi} registri ed elenchi che nominano troppi soggetti.
          </>
        ) : s.fase === 'collego' || s.fase === 'finito' ? (
          `${s.fase === 'collego' ? `In corso: ${progress(s.fatti, s.totale)}` : `Fatto: ${s.fatti} letti`} · ${s.legami} legami · ${s.nuove_voci} voci nuove${s.errori ? ` · ${s.errori} non riusciti` : ''}`
        ) : null
      }
    />
  );
}

/**
 * Pulizia dei dati (ADR 0192): profili con formati e identità, valori scritti in un modo solo, voci con nomi che non sono
 * nomi tolte, doppioni uniti; poi tutti i tipi tornano da rileggere (la rilettura ha la sua stima).
 */
function CleanupPanel() {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const key = ['tenant', tenant.id, 'data-model', 'cleanup'];
  const q = useTenantQuery<DataCleanupStateDto>(['data-model', 'cleanup'], '/data-model/cleanup', { refetchInterval: (s) => (s?.fase === 'in_corso' ? 3000 : false) });
  const start = useMutation({
    mutationFn: () => api<DataCleanupStateDto>(t('/data-model/cleanup/start'), { body: {} }),
    onSuccess: (s) => queryClient.setQueryData(key, s),
  });
  const s = q.data;
  const finished = s?.fase === 'finito' ? s.aggiornato_il : null;
  // Finita la pulizia, i tipi e le voci si ricaricano una volta.
  useEffect(() => {
    if (finished) void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'data-model'] });
  }, [finished, queryClient, tenant.id]);
  if (!s) return null;
  const busy = s.fase === 'in_corso' || start.isPending;
  return (
    <Card>
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold">Pulisci i dati</h2>
          <p className="mt-0.5 text-xs text-ink/60">
            Ogni tipo riceve i campi che riconoscono una voce e il formato di ogni dato (IBAN, codice fiscale, provincia, importo…). I valori si riscrivono in un modo solo, le voci il cui nome è un importo, una frase o un’operazione si tolgono, quelle con la stessa identità si uniscono. Ciò che hai scritto tu non si tocca. Poi tutti i tipi tornano tra quelli da rileggere, con la loro stima.
          </p>
          <div className="mt-2 text-xs">
            {s.fase === 'in_corso'
              ? 'Pulizia in corso…'
              : s.fase === 'finito' && s.esito
                ? `Fatto: ${s.esito.valori} voci corrette o tolte · ${s.esito.unite} doppioni uniti · ${s.esito.da_rileggere} tipi da rileggere`
                : null}
          </div>
          <ErrorText>{s.errore ?? (start.error ? errorMessage(start.error) : '')}</ErrorText>
        </div>
        <Button
          variant="ghost"
          disabled={busy}
          onClick={() => {
            if (window.confirm('Pulire i dati dell’azienda? Costa pochi centesimi; la rilettura dei documenti dopo ha la sua stima.')) start.mutate();
          }}
        >
          {busy ? 'Pulisco…' : 'Pulisci'}
        </Button>
      </div>
    </Card>
  );
}

/** Rilettura dei documenti e della posta già letti per i tipi nati dopo (ADR 0149). */
function RereadPanel({ pending }: { pending: DataModelState['da_rileggere'] }) {
  return (
    <JobPanel<TypeRereadStateDto>
      path="reread"
      title="Tipi nuovi da cercare nelle letture già fatte"
      intro={`${
        pending.length ? `Questi tipi sono nati dopo documenti e posta già letti, che quindi non li cercavano: ${pending.map((x) => x.etichetta).join(', ')}. ` : ''
      }Il sistema trova le letture che probabilmente li riguardano e, se lo confermi, il modello economico le rilegge solo per questi tipi.`}
      running="rileggo"
      estimating="stima"
      stopped="fermo"
      hidden={(s) => !pending.length && s.fase === 'nessuna'}
      summary={(s) =>
        s.fase === 'stima' ? (
          'Cerco le letture che riguardano i tipi nuovi…'
        ) : s.fase === 'pronto' && s.stima ? (
          s.totale ? (
            <>
              Da rileggere: <b>{s.stima.documenti}</b> documenti e <b>{s.stima.messaggi}</b> messaggi (circa {Math.round(s.stima.token / 1000)} mila token,{' '}
              <b>circa ${s.stima.costo_usd.toFixed(2)}</b>) per {s.stima.tipi.map((x) => `${x.etichetta} (${x.documenti + x.messaggi})`).join(', ')}.
            </>
          ) : (
            'Nessuna lettura già fatta riguarda i tipi nuovi: non c’è niente da rileggere.'
          )
        ) : s.fase === 'rileggo' || s.fase === 'fermo' || s.fase === 'finito' ? (
          `${s.fase === 'rileggo' ? `In corso: ${progress(s.fatti, s.totale)}` : s.fase === 'fermo' ? `Fermata a ${progress(s.fatti, s.totale)}` : `Fatto: ${s.fatti} rilette`} · ${s.voci} voci trovate${s.errori ? ` · ${s.errori} non riuscite` : ''}`
        ) : null
      }
    />
  );
}

/** Completamento dei valori mancanti delle voci (ADR 0073). */
function CompletionPanel() {
  return (
    <JobPanel<CompletionStateDto>
      path="completion"
      title="Valori mancanti da cercare"
      intro="Ogni voce ha il profilo completo del suo tipo. Per i valori che mancano il modello economico legge i documenti e i messaggi della voce e tiene solo ciò che vi è scritto, con la fonte."
      running="completo"
      summary={(s) =>
        s.fase === 'pronto' && s.stima ? (
          <>
            <b>{s.stima.voci}</b> voci con <b>{s.stima.valori_mancanti}</b> valori mancanti (al massimo circa {Math.round(s.stima.token / 1000)} mila token,{' '}
            <b>al massimo ${s.stima.costo_usd.toFixed(2)}</b>: le voci senza documenti non costano).
          </>
        ) : s.fase === 'completo' || s.fase === 'finito' ? (
          `${s.fase === 'completo' ? `In corso: ${progress(s.fatti, s.totale)}` : `Fatto: ${s.fatti} voci`} · ${s.valori} valori trovati · ${s.spostate} spostate nel rapporto giusto · ${s.senza_fonti} senza documenti${s.errori ? ` · ${s.errori} non riuscite` : ''}`
        ) : null
      }
    />
  );
}

type Filter = { site: string; activity: string };

/** Ricerca e filtro per sede e attività degli elenchi (ADR 0057). */
function Filters({
  search,
  onSearch,
  sites,
  filter,
  onFilter,
  placeholder,
}: {
  /** Senza `onSearch` niente casella: nella pagina del tipo si cerca nelle colonne della tabella. */
  search?: string;
  onSearch?: (v: string) => void;
  sites: Site[];
  filter: Filter;
  onFilter: (f: Filter) => void;
  placeholder?: string;
}) {
  const filterSite = sites.find((x) => x.id === filter.site);
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      {onSearch && (
        <input
          className="w-64 rounded-lg border border-black/15 bg-white px-3 py-1.5 text-sm outline-none focus:border-accent"
          placeholder={placeholder}
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      )}
      {sites.length > 0 && (
        <>
          <span className={`${onSearch ? 'ml-2 ' : ''}text-ink/60`}>Mostra:</span>
          <select className="rounded border border-black/15 bg-white px-2 py-1" value={filter.site} onChange={(e) => onFilter({ site: e.target.value, activity: '' })}>
            <option value="">tutte le sedi</option>
            {sites.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
            <option value="da_attribuire">senza sede o attività</option>
          </select>
          {filterSite && filterSite.activities.length > 0 && (
            <select className="rounded border border-black/15 bg-white px-2 py-1" value={filter.activity} onChange={(e) => onFilter({ ...filter, activity: e.target.value })}>
              <option value="">tutte le attività</option>
              {filterSite.activities.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          )}
        </>
      )}
    </div>
  );
}

/** Pagina di un tipo del menu laterale (ADR 0112): l'elenco aperto, con ricerca e filtro; il nome apre la scheda. */
export function EntityTypePage({ typeKey }: { typeKey: string }) {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();
  const q = useTenantQuery<DataModelState>(['data-model'], '/data-model', { refetchInterval: (s) => (s?.generating ? 3000 : false) });
  const required = useTenantQuery<RequiredDocumentsState>(['archive', 'required'], '/archive/required');
  const sitesQ = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  const sites = (sitesQ.data?.data ?? []).filter((x) => x.status !== 'chiusa');
  const [filter, setFilter] = useState<Filter>({ site: '', activity: '' });
  const setState = (s: DataModelState) => {
    queryClient.setQueryData(['tenant', tenant.id, 'data-model'], s);
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'archive', 'required'] });
  };
  if (q.isLoading) return <SectionLoader />;
  const type = q.data?.data.find((x) => x.chiave === typeKey);
  if (!type)
    return (
      <Card>
        <ErrorText>{q.error ? errorMessage(q.error) : 'Questo tipo non c’è più nel modello dell’azienda.'}</ErrorText>
        <Button variant="ghost" className="mt-2" onClick={() => navigate(`/t/${tenant.id}/dati`)}>
          Com’è fatta l’azienda
        </Button>
      </Card>
    );
  return (
    <div className="space-y-4">
      <PageTitle title={type.etichetta} subtitle={type.descrizione ?? undefined} />
      {sites.length > 0 && <Filters sites={sites} filter={filter} onFilter={setFilter} />}
      <TypeCard type={type} required={required.data} sites={sites} filter={filter} query="" onState={setState} alwaysOpen allTypes={q.data?.data} />
    </div>
  );
}

/** Pagina per aggiungere una voce di un tipo: alla conferma apre la scheda della voce nuova, «Annulla» torna all'elenco. */
export function NewEntityPage({ typeKey }: { typeKey: string }) {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();
  const q = useTenantQuery<DataModelState>(['data-model'], '/data-model');
  const sitesQ = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  const sites = (sitesQ.data?.data ?? []).filter((x) => x.status !== 'chiusa');
  const back = () => navigate(`/t/${tenant.id}/e/${typeKey}`);
  if (q.isLoading) return <SectionLoader />;
  const type = q.data?.data.find((x) => x.chiave === typeKey);
  if (!type)
    return (
      <Card>
        <ErrorText>{q.error ? errorMessage(q.error) : 'Questo tipo non c’è più nel modello dell’azienda.'}</ErrorText>
      </Card>
    );
  const saved = (s: DataModelState) => {
    queryClient.setQueryData(['tenant', tenant.id, 'data-model'], s);
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'archive', 'required'] });
    const before = new Set(type.entita.map((x) => x.rif));
    const added = s.data.find((x) => x.chiave === typeKey)?.entita.find((x) => !before.has(x.rif));
    if (added) navigate(`/t/${tenant.id}/e/${typeKey}/${encodeURIComponent(added.rif)}`);
    else back();
  };
  return (
    <Card className="p-0">
      <div className="border-b border-black/5 px-6 py-5">
        <button className="text-xs font-medium text-accent hover:underline" onClick={back}>
          ← {type.etichetta}
        </button>
        <h1 className="mt-1 text-xl font-semibold">Nuova voce in {type.etichetta.toLowerCase()}</h1>
        {type.descrizione && <p className="mt-0.5 text-sm text-ink/55">{type.descrizione}</p>}
      </div>
      <div className="p-5">
        <EntityForm type={type} entity={null} sites={sites} onSaved={saved} onCancel={back} />
      </div>
    </Card>
  );
}

/** Dati dell'azienda (ADR 0005): i tipi di cose che gestisce, ricavati dal sistema, con il loro elenco. */
export function DataModelPage() {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const q = useTenantQuery<DataModelState>(['data-model'], '/data-model', { refetchInterval: (s) => (s?.generating ? 3000 : false) });
  const required = useTenantQuery<RequiredDocumentsState>(['archive', 'required'], '/archive/required');
  const sitesQ = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  const sites = (sitesQ.data?.data ?? []).filter((x) => x.status !== 'chiusa');
  const [filter, setFilter] = useState({ site: '', activity: '' });
  const [search, setSearch] = useState('');
  const query = fold(search.trim());
  const setState = (s: DataModelState) => {
    queryClient.setQueryData(['tenant', tenant.id, 'data-model'], s);
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'archive', 'required'] });
  };
  const generate = useMutation({ mutationFn: () => api<DataModelState>(t('/data-model/generate'), { body: {} }), onSuccess: setState });
  const s = q.data;
  // In ordine alfabetico; i tipi che non servono in fondo.
  const types = [...(s?.data ?? [])].sort(
    (a, b) => Number(a.stato !== 'attivo') - Number(b.stato !== 'attivo') || a.etichetta.localeCompare(b.etichetta, 'it', { sensitivity: 'base' }),
  );
  const anyMatch =
    !query || types.some((x) => fold(x.etichetta).includes(query) || fold(x.descrizione).includes(query) || x.entita.some((e) => entityMatches(e, query)));
  return (
    <div className="space-y-4">
      <PageTitle
        title="Com’è fatta l’azienda"
        subtitle={`I luoghi, i beni e le altre cose di ${tenant.name}, con i loro legami: il sistema le ricava dal settore e le censisce da documenti, posta e risposte; tu completi e confermi gli elenchi. Da ogni cosa e dalle sue caratteristiche dipendono documenti, obblighi e procedure.${s?.generated_at ? ` Aggiornato ${dateTime(s.generated_at)}.` : ''}`}
        actions={
          isManager && s && !s.needs_profile ? (
            <Button variant="ghost" disabled={s.generating || generate.isPending} onClick={() => generate.mutate()}>
              {s.generating ? 'Ricavo il modello…' : 'Aggiorna'}
            </Button>
          ) : undefined
        }
      />
      <ErrorText>{q.error ? errorMessage(q.error) : generate.error ? errorMessage(generate.error) : (s?.error ?? '')}</ErrorText>
      {s?.needs_profile ? (
        <Card>
          <p className="text-sm text-ink/60">
            Per ricavare i dati il sistema deve prima conoscere l’azienda.{' '}
            {isManager && (
              <button className="text-accent underline" onClick={() => navigate(`/t/${tenant.id}/configurazione`)}>
                Vai alla configurazione
              </button>
            )}
          </p>
        </Card>
      ) : !types.length ? (
        <Card>
          <p className="text-sm text-ink/60">{s?.generating ? 'Sto capendo quali cose gestisce l’azienda…' : isManager ? 'Nessun dato ancora: usa “Aggiorna”.' : 'Nessun dato ancora.'}</p>
        </Card>
      ) : (
        <>
          {isManager && <CleanupPanel />}
          {isManager && <RereadPanel pending={s?.da_rileggere ?? []} />}
          {isManager && <DocumentLinksPanel />}
          {isManager && <CompletionPanel />}
          <Filters search={search} onSearch={setSearch} sites={sites} filter={filter} onFilter={setFilter} placeholder="Cerca tra i tipi e al loro interno…" />
          {types.map((type) => (
            <TypeCard key={type.chiave} type={type} required={required.data} sites={sites} filter={filter} query={query} onState={setState} allTypes={s?.data} />
          ))}
          {!anyMatch && <p className="text-sm text-ink/55">Nessun tipo e nessun elemento contiene «{search.trim()}».</p>}
        </>
      )}
    </div>
  );
}
